import React, { useRef, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Target, Layers, Megaphone, Cpu } from "lucide-react";

const serviceIcons = {
  strategy: <Target size={30} strokeWidth={1.5} color="#ffc84d" />,
  contentCreation: <Layers size={30} strokeWidth={1.5} color="#f65151" />,
  campaigns: <Megaphone size={30} strokeWidth={1.5} color="#5170ff" />,
  webAutomation: <Cpu size={30} strokeWidth={1.5} color="#ffc84d" />,
};

const ServiceCard = ({ iconKey, title, text, delay }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        } else {
          setInView(false);
        }
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        flex: "1 1 200px",
        padding: "0 20px",
        borderLeft: "2px solid rgba(51,51,51,0.1)",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(40px)",
        transition: inView
          ? `opacity 0.65s ease ${delay * 150}ms, transform 0.65s ease ${delay * 150}ms`
          : "none",
      }}
    >
      <div style={{ marginBottom: "16px" }}>{serviceIcons[iconKey]}</div>
      <h3 style={{
        fontFamily: "'Monotype Corsiva', 'Cormorant Garamond', cursive",
        fontStyle: "italic",
        fontWeight: 600,
        fontSize: "20px",
        color: "#333",
        margin: "0 0 14px 0",
        letterSpacing: "0.02em",
      }}>
        {title}
      </h3>
      <p style={{
        fontFamily: "'Poppins', sans-serif",
        fontSize: "14px",
        color: "#555",
        lineHeight: 1.75,
        margin: 0,
      }}>
        {text}
      </p>
    </div>
  );
};

const AboutSection = () => {
  const { t } = useLanguage();
  const ab = t.about;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <section
      data-testid="about-section"
      style={{
        position: "relative",
        padding: isMobile ? "80px 24px 100px" : "80px 48px 100px",
        overflow: "hidden",
        background: "#fafafa",
      }}
    >
      {/* Animated blob 1 — yellow */}
      <div style={{
        position: "absolute",
        width: "580px", height: "520px",
        top: "-100px", left: "-130px",
        background: "rgba(255, 200, 77, 0.80)",
        borderRadius: "50%",
        filter: "blur(88px)",
        animation: "blobFloat1 18s ease-in-out infinite",
        zIndex: 0,
        pointerEvents: "none",
        willChange: "transform",
      }} />
      {/* Animated blob 2 — red */}
      <div style={{
        position: "absolute",
        width: "520px", height: "460px",
        top: "10%", right: "-110px",
        background: "rgba(246, 81, 81, 0.75)",
        borderRadius: "50%",
        filter: "blur(82px)",
        animation: "blobFloat2 24s ease-in-out infinite",
        zIndex: 0,
        pointerEvents: "none",
        willChange: "transform",
      }} />
      {/* Bottom fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "100px", zIndex: 1,
        background: "linear-gradient(to bottom, transparent, #fafafa)",
      }} />

      <div style={{ position: "relative", zIndex: 2, maxWidth: "1200px", margin: "0 auto" }}>
        {/* Row 1: Logo + Tagline */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr",
          gap: "40px",
          alignItems: "center",
          marginBottom: "64px",
        }}>
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_brand-identity-dev-2/artifacts/fkw9y91b_logo%20vidi%20communication.png"
              alt="ViDi Communications"
              loading="lazy"
              decoding="async"
              style={{
                height: "150px",
                width: "150px",
                objectFit: "contain",
                borderRadius: "18px",
                display: "block",
              }}
            />
          </div>
          <div>
            <p style={{
              fontFamily: "'Archivo Black', 'Arial Black', sans-serif",
              fontWeight: 900,
              fontSize: isMobile ? "20px" : "clamp(18px, 2.2vw, 28px)",
              color: "#333",
              lineHeight: 1.35,
              margin: 0,
              letterSpacing: "-0.5px",
            }}>
              {ab.tagline}
            </p>
          </div>
        </div>

        {/* Row 2: 4 Service Columns */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          paddingTop: "48px",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "32px" : "0",
        }}>
          <ServiceCard iconKey="strategy" title={ab.strategy.title} text={ab.strategy.text} delay={0} />
          <ServiceCard iconKey="contentCreation" title={ab.contentCreation.title} text={ab.contentCreation.text} delay={1} />
          <ServiceCard iconKey="campaigns" title={ab.campaigns.title} text={ab.campaigns.text} delay={2} />
          <ServiceCard iconKey="webAutomation" title={ab.webAutomation.title} text={ab.webAutomation.text} delay={3} />
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
